import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiModalConfirm } from "@miplatform/components";
import {
  ATTEMPT_DERIVED_STATUS,
  ATTEMPT_STATUSES,
  ICON_VARIANTS,
  ISSUE_STATUSES,
} from "@miplatform/constants";
import { Colors } from "@miplatform/types";
import { getContrastColor, isEmpty } from "@miplatform/utils";
import { mdiFormatListChecks, mdiPlus } from "@mdi/js";
import { Box, Collapse, Fade, Typography } from "@mui/material";
import { AttemptNumberBadge } from "../AttemptNumberBadge/AttemptNumberBadge";
import { IssueCard } from "../IssueCard/IssueCard";
import { Closingout } from "../Tabs/Issues/Closingout/Closingout";
import { IssueEditor } from "../Tabs/Issues/IssueEditor/IssueEditor";
import { CLOSEOUT_SCREENS as SCREENS } from "../constants";
import { IssueActionRequired } from "./IssueActionRequired";

export const IssueList = memo(
  ({
    isLoading,
    isViewable = false,
    attempt = {},
    users = [],
    issueTitles = [],
    hideAddNewIssueButton = false,
    getAttempt = () => {},
    setActiveScreen = () => {},
    setSelectedIssue = () => {},
    highlight = false,
    readOnly = false,
    closeoutAttempt = () => {},
    inProgressAttempt = () => {},
    testId = "issue-list",
    onViewClick,
  }) => {
    const {
      assignment: {
        order: {
          orderTemplateColor,
          orderTemplateAbbreviation,
          orderTemplateName,
        } = {},
        assignmentNumber,
      } = {},
      attemptNumber,
      issues,
    } = attempt;
    const [isViewMode, setViewMode] = useState(false);
    const [issueIndex, setIssueIndex] = useState(0);
    const haveIssues = !isEmpty(issues);

    const { classes, theme } = useStyles({
      orderTemplateColor,
      orderTemplateAbbreviation,
      highlight,
      haveIssues,
    });

    const [isIssueEditorOpen, setIsIssueEditorOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
      useState(false);

    const closeoutActionsRequired = useMemo(
      () =>
        [
          ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT,
          ATTEMPT_DERIVED_STATUS.JOB_COMPLETE,
          ATTEMPT_DERIVED_STATUS.JOB_INCOMPLETE,
        ].includes(attempt.derivedStatus)
          ? issues?.reduce(
              (acc, issue) =>
                acc +
                ![ISSUE_STATUSES.CANCELLED, ISSUE_STATUSES.CLOSED].includes(
                  issue.status
                ),
              0
            )
          : 0,
      [attempt.attemptStatus, issues]
    );
    const prebillActionsRequired = useMemo(
      () =>
        attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_PREBILL
          ? issues?.reduce((acc, issue) => {
              if (issue.invoiceItem?.subItems?.length > 0) {
                return acc + 1;
              }

              return acc;
            }, 0)
          : 0,
      [attempt.attemptStatus, issues]
    );

    const handleEditIssue = (issue) => () => {
      setSelectedIssue(issue);
      setActiveScreen(SCREENS.CLOSEOUT);
    };

    const handleEditorOpen = useCallback(() => {
      setIsIssueEditorOpen(true);
    }, []);

    const handleEditorClose = useCallback(() => {
      setIsIssueEditorOpen(false);
    }, []);

    const handleViewModeOn = useCallback(() => {
      setIssueIndex(0);
      setViewMode(true);
    }, []);

    const handleViewModeOff = useCallback(() => {
      setViewMode(false);
    }, []);

    useEffect(() => {
      handleEditorClose();
    }, [attempt?.id]);

    const handleIssueActionClick = () => {
      if (attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_PREBILL) {
        setActiveScreen(SCREENS.PREBILL);
      } else {
        if (
          [
            ATTEMPT_DERIVED_STATUS.JOB_COMPLETE,
            ATTEMPT_DERIVED_STATUS.JOB_INCOMPLETE,
          ].includes(attempt.derivedStatus)
        ) {
          inProgressAttempt();
        }

        if (haveIssues) {
          setActiveScreen(SCREENS.CLOSEOUT);
          return;
        }

        setIsConfirmationModalOpen(true);
      }
    };
    const inClosed = attempt.attemptStatus !== ATTEMPT_STATUSES.CLOSED;

    return isViewMode ? (
      <Closingout
        readOnly
        attempt={attempt}
        users={users}
        issueIndex={issueIndex}
        setIssueIndex={setIssueIndex}
        onCancel={handleViewModeOff}
      />
    ) : (
      <Box data-testId={testId}>
        {!readOnly && (
          <IssueActionRequired
            derivedStatus={attempt.derivedStatus}
            numOfActions={
              attempt.derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_PREBILL
                ? prebillActionsRequired
                : closeoutActionsRequired
            }
            orderTemplateName={orderTemplateName}
            assignmentNumber={assignmentNumber}
            isActionDisabled={isLoading}
            onActionClick={handleIssueActionClick}
          />
        )}
        <Box className={classes.root}>
          <Box className={classes.commonContainer}>
            <Box className={classes.assignmentTemplateBadge}>
              {orderTemplateAbbreviation}
            </Box>
            <Box className={classes.commonContainer} flex={1}>
              {(assignmentNumber || orderTemplateName) && (
                <Typography
                  className={classes.orderTemplateName}
                  data-testId={`order-number-${
                    assignmentNumber || orderTemplateName
                  }`}
                >
                  {`${assignmentNumber || ""}: ${orderTemplateName || ""}`}
                </Typography>
              )}
              <AttemptNumberBadge attemptNumber={attemptNumber} />
            </Box>
            <Fade in={!isIssueEditorOpen}>
              <Box>
                {!hideAddNewIssueButton && !readOnly && inClosed && (
                  <MiButton
                    title="Add New Task"
                    className={classes.textButton}
                    startIcon={{ path: mdiPlus }}
                    onClick={handleEditorOpen}
                  />
                )}
              </Box>
            </Fade>
            {(onViewClick ||
              (isViewable &&
                attempt.attemptStatus === ATTEMPT_STATUSES.CLOSED)) && (
              <MiButton
                title="View"
                size="small"
                className={classes.textButton}
                onClick={onViewClick || handleViewModeOn}
              />
            )}
          </Box>
          <Box className={classes.cardsContainer}>
            {issues?.map((issue, i) => (
              <IssueCard
                key={issue.id}
                testId={`issue-card-${issue.id}`}
                issue={issue}
                issueIndex={i + 1}
                readOnly={
                  readOnly ||
                  [
                    ATTEMPT_DERIVED_STATUS.CLOSEOUT_CLOSED,
                    ATTEMPT_DERIVED_STATUS.INVOICED,
                    ATTEMPT_DERIVED_STATUS.PENDING_INVOICING,
                    ATTEMPT_DERIVED_STATUS.PENDING_PREBILL,
                  ].includes(attempt.derivedStatus)
                }
                handleEdit={handleEditIssue(issue)}
              />
            ))}
          </Box>
          <Collapse in={isIssueEditorOpen} unmountOnExit>
            <IssueEditor
              orderId={attempt.orderId}
              orderTemplateId={attempt.orderTemplateId}
              assignmentId={attempt.assignment?.id}
              issueTitles={issueTitles}
              getAttempt={getAttempt}
              onCancel={handleEditorClose}
            />
          </Collapse>
        </Box>
        <MiModalConfirm
          title="Confirm Order Closure"
          titleIcon={{
            path: mdiFormatListChecks,
            color: theme.palette.primary.main,
            variant: ICON_VARIANTS.CONTAINED,
          }}
          textPositive="Yes, Continue"
          open={isConfirmationModalOpen}
          handleNegative={() => setIsConfirmationModalOpen(false)}
          handlePositive={() => {
            closeoutAttempt();
            setIsConfirmationModalOpen(false);
          }}
          description={`Are you sure you want to closeout ${assignmentNumber}?`}
          buttonPositiveColor={Colors.Primary}
        />
      </Box>
    );
  }
);

const useStyles = makeStyles()(
  (
    theme,
    { orderTemplateColor, orderTemplateAbbreviation, highlight, haveIssues }
  ) => ({
    root: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey.mediumLight,
      border: `0.5px solid ${theme.palette.cyan.dark}`,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      borderRadius: theme.spacing(1),
      width: "100%",
      ...(highlight
        ? { boxShadow: `0px 0px 5px 5px ${theme.palette.orange.mediumLight}` }
        : {}),
    },
    assignmentTemplateBadge: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: theme.spacing(0.5),
      width: 12 + (orderTemplateAbbreviation?.length || 0) * 3,
      height: 18,
      fontSize: theme.fontSize.xxsmall,
      backgroundColor: orderTemplateColor,
      fontWeight: theme.fontWeight.semiBold,
      color: getContrastColor(orderTemplateColor),
    },
    orderTemplateName: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.fontWeight.semiBold,
      color: theme.palette.bluegrey.dark,
    },
    beginCloseoutTexts: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    beginCloseoutContainer: {
      boxSizing: "content-box",
      backgroundColor: haveIssues
        ? theme.palette.error.main
        : theme.palette.green.main,
      borderRadius: 4,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    titleLine: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    cardsContainer: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    commonContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    countContainer: {
      borderRadius: 50,
      backgroundColor: theme.palette.common.white,
      width: 20,
      height: 20,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    count: {
      ...theme.typography.body2,
      color: haveIssues ? theme.palette.error.main : theme.palette.green.main,
    },
    beginCloseoutBox: {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      display: "flex",
      justifyContent: "space-between",
      padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    textButton: {
      borderRadius: 0,
      justifyContent: "flex-start",
      color: "primary.main",
      padding: theme.spacing(0, 2, 0, 2),
    },
  })
);
